import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { ArrowLeft, Download, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Gated, purchased-user-only page showing the AI Growth Analysis markdown.
 */
const GrowthReportPage = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [md, setMd] = useState(null);
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = `/login?next=/growth-report/${slug}`;
      return;
    }
    const attempt = async (tries = 0) => {
      try {
        const r = await axios.get(`${API_URL}/api/growth/report/${slug}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMd(r.data.markdown);
        setName(r.data.name);
      } catch (e) {
        // Stripe webhook may lag a few seconds after redirect — retry 3x
        if (e.response?.status === 402 && sessionId && tries < 3) {
          setTimeout(() => attempt(tries + 1), 2000);
          return;
        }
        setError(e.response?.data?.detail || 'Could not load report');
      } finally {
        if (tries >= 3 || md) setLoading(false);
        else setLoading(false);
      }
    };
    attempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const copyPlain = () => {
    if (!md) return;
    navigator.clipboard.writeText(md);
    toast.success('Report copied to clipboard');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="growth-report-page">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <Link to={`/properties-in/${slug}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#E07A3A] mb-6">
          <ArrowLeft className="h-4 w-4" />Back to {name || slug}
        </Link>

        {loading && (
          <div className="flex items-center gap-3 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" />Loading your AI Growth Analysis…</div>
        )}

        {error && (
          <div className="luxury-card p-8 border-l-4 border-[#E07A3A] bg-white" data-testid="growth-report-error">
            <Lock className="h-6 w-6 text-[#E07A3A] mb-3" />
            <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Report not yet unlocked</h2>
            <p className="text-slate-600 text-sm mb-4">{error}</p>
            <Link to={`/properties-in/${slug}`} className="inline-flex items-center gap-2 bg-[#E07A3A] text-white hover:bg-[#C4652E] text-sm tracking-widest uppercase px-6 py-3 font-semibold">
              Go purchase the report
            </Link>
          </div>
        )}

        {md && (
          <>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <span className="text-[10px] tracking-[0.4em] text-[#E07A3A] uppercase font-semibold">AI Growth Analysis</span>
                <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#1A1A1A]">{name}</h1>
              </div>
              <button onClick={copyPlain} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 hover:text-[#E07A3A] border border-slate-200 hover:border-[#E07A3A] px-4 py-2">
                <Download className="h-3 w-3" />Copy plain text
              </button>
            </div>
            <div className="luxury-card bg-white border border-slate-200 p-7 sm:p-10 prose prose-slate max-w-none text-[15px] leading-relaxed whitespace-pre-wrap" data-testid="growth-report-body">
              {md}
            </div>
            <p className="text-[11px] text-slate-400 mt-6">
              Shopperden is an AI Real Estate Network — not an estate agent or brokerage. This report is informational only. Always seek independent professional advice before any property transaction.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default GrowthReportPage;
